/**
 * Base place type filter (data-prop-default-place-type).
 *
 * Restricts the places a user can pick to terminals or to cities while hybrid search is off.
 * The rules match saas-app (`placeSelectionPolicy.ts`, `parseSearchDefaultPlaceType.ts`).
 */

export const BASE_PLACE_TYPES = Object.freeze({ TERMINAL: 'terminal', CITY: 'city' });

const isCityPlace = place => place?.result_type === 'city' || place?.resultType === 'city';

/**
 * Reads data-prop-default-place-type strictly: no trimming and no case folding.
 * @param {unknown} value - Attribute value
 * @returns {'terminal'|'city'|null} The type, or null when the value is anything else (no filter)
 */
export function parseBasePlaceType(value) {
  return value === BASE_PLACE_TYPES.TERMINAL || value === BASE_PLACE_TYPES.CITY ? value : null;
}

/**
 * The base type only applies while hybrid search is off. This is the only place that knows it.
 * @param {{ basePlaceType?: unknown, hybridSearchEnabled?: boolean }} options
 * @returns {'terminal'|'city'|null} The type to filter by, or null for no filter
 */
export function getActiveBasePlaceType({ basePlaceType, hybridSearchEnabled } = {}) {
  return hybridSearchEnabled ? null : parseBasePlaceType(basePlaceType);
}

/**
 * `city` keeps cities. `terminal` keeps every place that is not a city, including a place
 * with no type.
 * @param {{ result_type?: string, resultType?: string }} place
 * @param {'terminal'|'city'|null} type
 * @returns {boolean}
 */
export function matchesBasePlaceType(place, type) {
  if (!type) return true;
  return type === BASE_PLACE_TYPES.CITY ? isCityPlace(place) : !isCityPlace(place);
}

/**
 * @param {Array} places
 * @param {'terminal'|'city'|null} type
 * @returns {Array} The same array when there is no type, otherwise the matching places
 */
export function filterByBasePlaceType(places, type) {
  if (!type) return places;
  return places.filter(place => matchesBasePlaceType(place, type));
}

/**
 * A recent search is visible only when both its origin and its destination match.
 * @param {{ origin?: object, destination?: object }} search
 * @param {'terminal'|'city'|null} type
 * @returns {boolean}
 */
export function isRecentSearchVisible({ origin, destination } = {}, type) {
  return matchesBasePlaceType(origin, type) && matchesBasePlaceType(destination, type);
}

/**
 * Picks the nearby place the field is allowed to show.
 * @param {Array} places - Places returned by near-to-user, nearest first
 * @param {'terminal'|'city'|null} type
 * @returns {object|undefined} The first matching place, or undefined when none matches
 */
export function pickGeolocationPlace(places, type) {
  if (!Array.isArray(places)) return undefined;
  return places.find(place => matchesBasePlaceType(place, type));
}
